import React from 'react';
import { useSelector } from 'react-redux';

import type { Company, CompanySetupStepsKey } from '../../../@types/Company';
import { setupStepOrder } from '../../../@types/Company';
import { validateCompanyStep } from '../../../store/AuthReducer';
import type { AppDispatch, RootState } from '../../../store/store';
import { getValidReferents } from '../../../utils/branding';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { showToast } from '../Notification';
import ProgressBar from '../ProgressBar';

const PROGRESS_BAR_INDEX: Partial<Record<CompanySetupStepsKey, number>> = {
  coproValidation: 0,
  addMissingCopros: 1,
  addressValidation: 2,
  transactionQualification: 3,
  // 4 = branding (no setup step key — handled outside SetupPage)
  collaboratorAttribution: 5,
  mailAlertSetup: 6,
};

type SetupPageProps = {
  step: CompanySetupStepsKey;
  title: string;
  children: React.ReactNode;
};

function isPreviousStepIncomplete(step: CompanySetupStepsKey, company: Company): boolean {
  if (step === 'collaboratorAttribution' || step === 'mailAlertSetup') {
    const hasReferent = getValidReferents(company).length > 0;
    if (!hasReferent) {
      return true;
    }
  }

  const stepIndex = setupStepOrder.indexOf(step);
  if (stepIndex <= 0) {
    return false;
  }

  const previousStep = setupStepOrder[stepIndex - 1];
  return previousStep ? !company.setupSteps?.[previousStep] : false;
}
  
export function SetupPage({ step, title, children }: SetupPageProps) {
  const company = useSelector((state: RootState) => state.auth.company);

  const currentStepIndex = PROGRESS_BAR_INDEX[step] ?? setupStepOrder.indexOf(step);

  // Early return if company or setupSteps is not available yet
  if (!company?.id || !company.setupSteps) {
    return (
      <div className="rounded-lg bg-white p-4 pt-5 2xl:pt-8">
        <ProgressBar currentStep={currentStepIndex} />
        <h2 className="mt-10 px-2 font-semibold">
          <span className="text-rgpd-blue-light">
            PARAMÉTRAGE
            {' '}
            :
          </span>
          {' '}
          {title}
        </h2>
        <div className="mt-3 mb-6 border-t-2 border-b-2 border-rgpd-blue-dark" />
        {children}
      </div>
    );
  }

  const blocked = isPreviousStepIncomplete(step, company);

  return (
    <div className="rounded-lg bg-white p-4 pt-5 2xl:pt-8">
      <ProgressBar currentStep={currentStepIndex} />
      <h2 className="mt-10 px-2 font-semibold">
        <span className="text-rgpd-blue-light">
          PARAMÉTRAGE
          {' '}
          :
        </span>
        {' '}
        {title}
      </h2>
      <div className="mt-3 mb-6 border-t-2 border-b-2 border-rgpd-blue-dark" />
      {blocked ? (
        <div className="relative">
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100/85">
            <p className="font-semibold text-red-500">Merci de configurer l'étape précédente</p>
          </div>
          <div className="pointer-events-none opacity-50">
            {children}
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );
}

export const updateSetupState = async(company: Company, jwt: string, step: CompanySetupStepsKey, dispatch: AppDispatch) => {
  // If setupSteps is null/undefined, still perform the update by creating a minimal payload
  if (!company?.id) { return; }

  const baseSteps = company?.setupSteps ?? {} as Company['setupSteps'];
  if (baseSteps && baseSteps[step]) { return; }

  try {
    const updatedSetupSteps = { ...baseSteps, [step]: true } as Company['setupSteps'];
    const response = await fetch(`/api/companies/${company.id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': JWTAuthorization(jwt),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        setupSteps: updatedSetupSteps,
      }),
    });

    if (!response.ok) {
      throw new Error('Error updating setup state');
    }
    dispatch(validateCompanyStep(step));
  } catch (error) {
    console.error('Error updating setup state :', error);
    showToast('error', 'Une erreur est survenue lors de la mise à jour des données.');
  }
};
